/**
 * Token storage utilities for the SignalSentry client app.
 *
 * Centralizes all localStorage read/write/clear operations for auth tokens.
 * All keys are string constants exported for testing and cross-module use.
 *
 * Storage decision: localStorage per Phase 3 CONTEXT.md "Auth token storage"
 * decision. A later phase may migrate to httpOnly cookies; this module
 * provides the abstraction boundary for that future change.
 *
 * Phase 3.1 (per ADR-006 section 3 token-swap primitive):
 * Added a sidecar staff token store. During staff impersonation
 * (POST /v1/admin/impersonate/<org_id>), the original staff access + refresh
 * tokens are saved under sidecar keys while the active token slots hold the
 * impersonation pair. exitImpersonation restores the staff pair from the
 * sidecar. The sidecar API is consumed by Phase 3.1 Plan 03 orchestration
 * helpers and by AuthProvider's bootstrap for stale-sidecar recovery.
 */

/** localStorage key for the short-lived (15 min) JWT access token. */
const ACCESS_KEY = 'signalsentry:access_token';

/** localStorage key for the 24h JWT refresh token. */
const REFRESH_KEY = 'signalsentry:refresh_token';

/** localStorage key for the saved staff access token during impersonation. */
const STAFF_ACCESS_KEY = 'signalsentry:staff_access_token';

/** localStorage key for the saved staff refresh token during impersonation. */
const STAFF_REFRESH_KEY = 'signalsentry:staff_refresh_token';

/**
 * Sentinel string stored in the active refresh-token slot during impersonation.
 *
 * Phase 3.1 Plan 03 (per ADR-006 + ADR-005):
 *   Impersonation access tokens minted by POST /v1/admin/impersonate/<org_id>
 *   have NO refresh token (per ADR-005 the 1-hour cap forbids refresh; staff
 *   must re-impersonate). The active refresh slot literally holds this sentinel
 *   string while impersonation is active.
 *
 *   Detection points:
 *   - api-client.ts refresh-on-401 path checks the active refresh value BEFORE
 *     calling POST /v1/auth/refresh. If it equals this sentinel, the 401 is
 *     treated as an impersonation access-token expiry (1-hour cap) and the
 *     client gracefully exits impersonation rather than calling /refresh
 *     (which would silently mint a fresh staff token and corrupt React state).
 *   - start-impersonation.ts guards against double-start by rejecting when
 *     the active refresh already equals this sentinel.
 *
 * The literal value MUST NOT collide with any real JWT shape. Real refresh
 * tokens are JWTs (three base64url segments separated by dots) that never
 * contain the substring "__IMPERSONATION_NO_REFRESH__".
 */
export const IMPERSONATION_NO_REFRESH = '__IMPERSONATION_NO_REFRESH__' as const;

/**
 * Typed auth token storage interface.
 * All read/write/clear operations go through this object.
 */
export const authStorage = {
  /** Returns the stored access token, or null if absent. */
  getAccess: (): string | null => localStorage.getItem(ACCESS_KEY),

  /** Returns the stored refresh token, or null if absent. */
  getRefresh: (): string | null => localStorage.getItem(REFRESH_KEY),

  /**
   * Stores both tokens atomically.
   * Called after a successful login or token refresh.
   *
   * @param access - New access token string
   * @param refresh - New refresh token string
   */
  set: (access: string, refresh: string): void => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },

  /**
   * Clears both tokens from storage.
   * Called on logout or when a refresh attempt fails.
   */
  clear: (): void => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
} as const;

/**
 * Sidecar storage for the original staff token pair during impersonation.
 *
 * Lifecycle (per ADR-006 section 3):
 *   1. startImpersonation (Phase 3.1 Plan 03):
 *      - read current authStorage tokens (the staff pair)
 *      - call staffSidecar.set(access, refresh) to save them
 *      - replace active tokens with the impersonation pair via authStorage.set
 *   2. exitImpersonation (Phase 3.1 Plan 03):
 *      - call staffSidecar.get() to retrieve the saved staff pair
 *      - replace active tokens via authStorage.set
 *      - call staffSidecar.clear() to remove the sidecar entries
 *   3. App bootstrap (Phase 3.1 Plan 03):
 *      - if staffSidecar.has() AND active token /me returns 401, fall back to
 *        the saved staff pair before routing to /login.
 *
 * The sidecar is a strict pair: get() returns null unless BOTH keys exist.
 * Writing one without the other is an internal error and should not happen
 * via this API.
 */
export const staffSidecar = {
  /**
   * Returns the saved staff token pair, or null if either key is missing.
   *
   * @returns Object with access and refresh strings, or null if no sidecar
   *          is currently saved.
   */
  get: (): { access: string; refresh: string } | null => {
    const access = localStorage.getItem(STAFF_ACCESS_KEY);
    const refresh = localStorage.getItem(STAFF_REFRESH_KEY);
    return access && refresh ? { access, refresh } : null;
  },

  /**
   * Saves the staff token pair to sidecar storage.
   * Called by startImpersonation before swapping the active token slots.
   *
   * @param access - Staff access token to preserve
   * @param refresh - Staff refresh token to preserve
   */
  set: (access: string, refresh: string): void => {
    localStorage.setItem(STAFF_ACCESS_KEY, access);
    localStorage.setItem(STAFF_REFRESH_KEY, refresh);
  },

  /**
   * Removes both sidecar entries. Called by exitImpersonation after the
   * active slots have been restored to the staff pair.
   */
  clear: (): void => {
    localStorage.removeItem(STAFF_ACCESS_KEY);
    localStorage.removeItem(STAFF_REFRESH_KEY);
  },

  /**
   * Returns true iff both sidecar keys are present in localStorage.
   * Read by the bootstrap effect in AuthProvider for stale-sidecar recovery.
   */
  has: (): boolean => {
    return (
      localStorage.getItem(STAFF_ACCESS_KEY) !== null &&
      localStorage.getItem(STAFF_REFRESH_KEY) !== null
    );
  },
} as const;

/**
 * Returns true iff the current active refresh slot holds the
 * IMPERSONATION_NO_REFRESH sentinel.
 *
 * Phase 3.1 Plan 03: this is the canonical "am I currently impersonating"
 * check from a token-storage perspective. The User object's
 * impersonating_as flag is the React-state-level signal; this helper is
 * the localStorage-level signal used by api-client and orchestration code
 * that must inspect storage without reading React context.
 */
export function isImpersonationActive(): boolean {
  return authStorage.getRefresh() === IMPERSONATION_NO_REFRESH;
}
